
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Loader2, FileText, AlertCircle } from "lucide-react";
import { EditForm } from "../hooks/useQuoteEdit";
import { PEOQuote } from "@/types/quotes.types";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useState, useEffect } from "react";

interface QuoteEditDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  editForm: EditForm;
  setEditForm: (form: EditForm) => void;
  selectedQuote: PEOQuote | null;
  isSubmitting: boolean;
  uploadError: string | null;
  onSave: () => void;
}

export const QuoteEditDialog = ({
  isOpen,
  onOpenChange,
  editForm,
  setEditForm,
  selectedQuote,
  isSubmitting,
  uploadError,
  onSave
}: QuoteEditDialogProps) => {
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  
  // Add update error state for display
  const [updateErrorMessage, setUpdateErrorMessage] = useState<string | null>(null);
  const [fileSelected, setFileSelected] = useState<boolean>(false);

  useEffect(() => {
    // Reset form errors when dialog opens or closes
    if (!isOpen) {
      setFormErrors({});
      setUpdateErrorMessage(null);
      setFileSelected(false);
    }
  }, [isOpen]);

  // Debug log when form values change
  useEffect(() => {
    if (isOpen) {
      console.log("Form values changed:", editForm);
    }
  }, [editForm, isOpen]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      console.log('File selected:', e.target.files[0].name);
      setEditForm({ ...editForm, quoteFile: e.target.files[0] });
      setFileSelected(true);
    }
  };

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    // Only validate monthly/annual cost if they're provided (they can be empty)
    if (editForm.monthlyAmount && isNaN(Number(editForm.monthlyAmount))) {
      errors.monthlyAmount = "Monthly amount must be a valid number";
    }
    
    if (editForm.annualAmount && isNaN(Number(editForm.annualAmount))) {
      errors.annualAmount = "Annual amount must be a valid number";
    }

    // Validate valid until date is in the future if provided
    if (editForm.validUntil) {
      const validDate = new Date(editForm.validUntil);
      if (isNaN(validDate.getTime())) {
        errors.validUntil = "Invalid date format";
      }
    }

    console.log("Form validation result:", errors);
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    console.log("Save button clicked with form data:", editForm);
    setUpdateErrorMessage(null);
    if (validateForm()) {
      try {
        onSave();
      } catch (error: any) {
        setUpdateErrorMessage(error.message || "Failed to save quote");
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-primary-dark text-xl">Edit Quote Details</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Update the quote details for {selectedQuote?.application?.company_legal_name || 'this application'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 py-4">
          {/* Display any update errors */}
          {updateErrorMessage && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Error: {updateErrorMessage}
              </AlertDescription>
            </Alert>
          )}
          
          <div className="grid grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="monthlyAmount" className="text-sm font-medium">Monthly Cost ($)</Label>
              <Input
                id="monthlyAmount"
                type="number"
                value={editForm.monthlyAmount}
                onChange={(e) => setEditForm({ ...editForm, monthlyAmount: e.target.value })}
                className={`border-gray-200 focus-visible:ring-primary ${formErrors.monthlyAmount ? 'border-red-500' : ''}`}
                step="0.01"
                min="0"
                placeholder="0.00"
              />
              {formErrors.monthlyAmount && (
                <p className="text-xs text-red-500">{formErrors.monthlyAmount}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="annualAmount" className="text-sm font-medium">Annual Cost ($)</Label>
              <Input
                id="annualAmount"
                type="number"
                value={editForm.annualAmount}
                onChange={(e) => setEditForm({ ...editForm, annualAmount: e.target.value })}
                className={`border-gray-200 focus-visible:ring-primary ${formErrors.annualAmount ? 'border-red-500' : ''}`}
                step="0.01"
                min="0"
                placeholder="0.00"
              />
              {formErrors.annualAmount && (
                <p className="text-xs text-red-500">{formErrors.annualAmount}</p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="validUntil" className="text-sm font-medium">Valid Until</Label>
            <Input
              id="validUntil"
              type="date"
              value={editForm.validUntil}
              onChange={(e) => setEditForm({ ...editForm, validUntil: e.target.value })}
              className={`border-gray-200 focus-visible:ring-primary ${formErrors.validUntil ? 'border-red-500' : ''}`}
            />
            {formErrors.validUntil && (
              <p className="text-xs text-red-500">{formErrors.validUntil}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="quoteFile" className="text-sm font-medium">Quote Document</Label>
            <div className="border border-dashed border-gray-300 rounded-md p-4 bg-gray-50 hover:bg-gray-100 transition-colors">
              <Input
                id="quoteFile"
                type="file"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx"
                className="border-0 focus-visible:ring-0 bg-transparent p-0"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Upload PDF, DOC or DOCX files only
              </p>
            </div>
            {uploadError && (
              <Alert variant="destructive" className="mt-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  Error: {uploadError}
                </AlertDescription>
              </Alert>
            )}
            {fileSelected && (
              <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                <FileText className="h-3 w-3" />
                New file selected
              </p>
            )}
            {selectedQuote?.quote_file_url && !fileSelected && (
              <p className="text-sm flex items-center gap-1 mt-2 text-primary">
                <FileText className="h-3 w-3" />
                <span>Current file:</span>
                <a href={selectedQuote.quote_file_url} target="_blank" rel="noopener noreferrer" className="underline hover:text-primary-dark">
                  View existing document
                </a>
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="border-gray-200 bg-white"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={isSubmitting}
            className="bg-primary hover:bg-primary/90"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
